import { sortBy } from 'lodash';

import {
  Row,
  Col,
  AutoComplete,
  Input,
  InputNumber,
  Select,
  Tooltip,
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';

import { Form, SectionWrapper, WrapperSelect } from 'common/components';

import { useGetProductEnums } from 'hooks';
import { SCREEN_WIDTH, useWindowSize } from 'hooks/windowSize';
import { useFactPanelQasRequiredFields } from '../../hook/useFactPanelQasRequiredFields';

import { SUPPLEMENT_SERVING_UMO } from '../../constants';
import { DEFAULT_FOOTER_NOTE_SUPPLEMENT } from '../../DailyValueConstants';

import Messages from 'i18n/messages/product';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from 'pages/qa-spec/components/shared/QaSpecHelper';

const FORM_ITEM_GUTTER = 8;

const SupplementForm = (props) => {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const { productId, typeView } = props;

  const { productEnums } = useGetProductEnums();

  const [screenWidth, _] = useWindowSize();

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.supplementLabel',
  });

  const labelTypeOptions = [
    {
      label: intl.formatMessage(Messages.supplementLabelTypeStandard),
      value: 'US_FDA_NFP_2020_STANDARD',
    },
  ];

  const isQaSpecType = typeView === 'qa-spec';

  const getServingUmoOptions = () => {
    const productBasicUOM = productEnums?.find(
      (type) => type.enumName === 'BasicUomEnum'
    );

    const enums = productBasicUOM.enumProperties
      .filter((type) => ['Mass', 'Volume'].includes(type.enumGroup))
      ?.map((item, index) => ({
        label: item.enumDisplayName,
        value: item.enumDisplayName,
      }));

    return sortBy([...enums, ...SUPPLEMENT_SERVING_UMO], 'label');
  };

  const handleFooterNote = () => {
    form.setFieldsValue({
      ...form.getFieldsValue(),
      fdaLabelFootNote: DEFAULT_FOOTER_NOTE_SUPPLEMENT,
    });
  };

  const servingUmoOptions = getServingUmoOptions();

  const languageCodeEnum = productEnums?.filter(
    (type) => type.enumName === 'LanguageCodeEnum'
  );

  const servingPerContainerDescriptorEnum = productEnums?.find(
    (type) => type.enumName === 'ServingPerContainerDescriptorEnum'
  );

  return (
    <div style={{ padding: '8px 0px' }}>
      <Form.Item
        name='nutrientFormatType'
        label={
          <TraceableCommentContainer
            qaSpecName={getFormattedPath(
              TOOlTIP_FIELDS.supplementTypeCode.fieldPath
            )}
            qaSpecFieldName={intl.formatMessage(
              Messages.supplementLabelTypeLabel
            )}
          >
            <QaSpecHelpContentLabel
              fieldPath={TOOlTIP_FIELDS.supplementTypeCode.fieldPath}
              isEnabled={isQaSpecType}
            >
              {intl.formatMessage(Messages.supplementLabelTypeLabel)}
            </QaSpecHelpContentLabel>
          </TraceableCommentContainer>
        }
        rules={[
          getRequiredRule({
            fieldName: ['nutrientFormatType'],
            displayName: 'Label Type',
          }),
        ]}
      >
        <Select
          allowClear
          options={labelTypeOptions}
          getPopupContainer={(triggerNode) => {
            return triggerNode;
          }}
        />
      </Form.Item>

      <SectionWrapper
        title={intl.formatMessage(Messages.supplementServingSize)}
        style={{ marginBottom: 10 }}
      >
        <Row wrap={false} gutter={FORM_ITEM_GUTTER}>
          <Col flex={1}>
            <Form.Item
              name='servingSize'
              label={
                <TraceableCommentContainer
                  qaSpecName={getFormattedPath(
                    TOOlTIP_FIELDS.supplementServingSize.fieldPath
                  )}
                  qaSpecFieldName={intl.formatMessage(
                    Messages.supplementServingSizeValueLabel
                  )}
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.supplementServingSize.fieldPath}
                    isEnabled={isQaSpecType}
                  >
                    {intl.formatMessage(
                      Messages.supplementServingSizeValueLabel
                    )}
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={[
                {
                  required: true,
                  message: 'Value is required',
                },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Col>
          <Col flex={1}>
            <Form.Item
              name='servingSizeMeasurement'
              label={
                <TraceableCommentContainer
                  qaSpecName='Fact Panels Supplement Information'
                  qaSpecFieldName={intl.formatMessage(
                    Messages.supplementServingSizeUmoLabel
                  )}
                >
                  <QaSpecHelpContentLabel
                    fieldPath={
                      TOOlTIP_FIELDS.measurementPrecisionCode.fieldPath
                    }
                    isEnabled={isQaSpecType}
                  >
                    {intl.formatMessage(Messages.supplementServingSizeUmoLabel)}
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={[
                {
                  required: true,
                  message: 'UOM is required',
                },
              ]}
            >
              <AutoComplete
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                options={servingUmoOptions}
                filterOption={(inputValue, option) => {
                  return (
                    option?.label
                      ?.toLowerCase()
                      ?.indexOf(inputValue?.toLowerCase()) > -1
                  );
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name='servingSizeDescription'
          label={
            <TraceableCommentContainer
              qaSpecName={getFormattedPath(
                TOOlTIP_FIELDS.supplementServingSizeDescription.fieldPath
              )}
              qaSpecFieldName={intl.formatMessage(
                Messages.supplementServingSizeDescriptionLabel
              )}
            >
              <QaSpecHelpContentLabel
                fieldPath={
                  TOOlTIP_FIELDS.supplementServingSizeDescription.fieldPath
                }
                isEnabled={isQaSpecType}
              >
                {intl.formatMessage(
                  Messages.supplementServingSizeDescriptionLabel
                )}
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
          }
          rules={[
            getRequiredRule({
              fieldName: ['servingSizeDescription'],
              displayName: intl.formatMessage(
                Messages.supplementServingSizeDescriptionLabel
              ),
            }),
          ]}
        >
          <Input />
        </Form.Item>
      </SectionWrapper>

      <Row gutter={FORM_ITEM_GUTTER}>
        <Col span={12}>
          <Form.Item
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.supplementServingPerContainerDescriptor
                    .fieldPath
                )}
                qaSpecFieldName='Servings Per Container Descriptor'
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.supplementServingPerContainerDescriptor
                      .fieldPath
                  }
                  isEnabled={isQaSpecType}
                >
                  {screenWidth > SCREEN_WIDTH.EXTRA_LARGE
                    ? 'Servings Per Container Descriptor'
                    : 'S/C Descriptor'}
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            name='servingPerContainerDescriptor'
            rules={[
              getRequiredRule({
                fieldName: ['servingPerContainerDescriptor'],
                displayName: 'S/C Descriptor',
              }),
            ]}
          >
            <WrapperSelect
              getPopupContainer={(triggerNode) => {
                return triggerNode;
              }}
            >
              {sortBy(servingPerContainerDescriptorEnum?.enumProperties, [
                'enumDisplayName',
                'enumGroup',
              ]).map((item, index) => (
                <Select.Option value={item?.enumDisplayName} key={index}>
                  {item?.enumDisplayName}
                </Select.Option>
              ))}
            </WrapperSelect>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='numberOfServingsPerContainer'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.supplementNumberOfServingsPerContainer
                    .fieldPath
                )}
                qaSpecFieldName='Servings Per Container'
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.supplementNumberOfServingsPerContainer
                      .fieldPath
                  }
                  isEnabled={isQaSpecType}
                >
                  {screenWidth > SCREEN_WIDTH.EXTRA_LARGE
                    ? 'Servings Per Container'
                    : 'S/C'}
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              {
                required: true,
                message: 'Serving Per Container is required',
              },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (Number(value) > 1000) {
                    return Promise.reject([
                      'Servings Per Container should be smaller than 1000.',
                    ]);
                  } else {
                    return Promise.resolve();
                  }
                },
              }),
            ]}
          >
            <InputNumber
              min={0}
              max={1000}
              formatter={(value) => {
                if (+value > 0)
                  return (+value)?.toFixed(4)?.replace(/(\.0+|0+)$/, '');
                return value;
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row wrap={false} gutter={FORM_ITEM_GUTTER}>
        <Col flex={1}>
          <Form.Item
            name='calories'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.supplementCalories.fieldPath
                )}
                qaSpecFieldName={intl.formatMessage(
                  Messages.supplementCalories
                )}
              >
                <QaSpecHelpContentLabel
                  fieldPath={TOOlTIP_FIELDS.supplementCalories.fieldPath}
                  isEnabled={isQaSpecType}
                >
                  {intl.formatMessage(Messages.supplementCalories)}
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['calories'],
                displayName: intl.formatMessage(Messages.supplementCalories),
              }),
            ]}
          >
            <InputNumber min={1} />
          </Form.Item>
        </Col>
        <Col flex={1}>
          <Form.Item shouldUpdate>
            {(formInst) => {
              const calories = formInst.getFieldValue('calories');

              return (
                <Form.Item
                  name='caloriesFromFat'
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        TOOlTIP_FIELDS.supplementCaloriesFromFat.fieldPath
                      )}
                      qaSpecFieldName={intl.formatMessage(
                        Messages.supplementCaloriesFat
                      )}
                    >
                      <QaSpecHelpContentLabel
                        fieldPath={
                          TOOlTIP_FIELDS.supplementCaloriesFromFat.fieldPath
                        }
                        isEnabled={isQaSpecType}
                      >
                        {intl.formatMessage(Messages.supplementCaloriesFat)}
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  rules={[
                    getRequiredRule({
                      fieldName: ['caloriesFromFat'],
                      displayName: intl.formatMessage(
                        Messages.supplementCaloriesFat
                      ),
                    }),
                  ]}
                >
                  <InputNumber min={0} disabled={!calories} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name='fdaLabelFootNote'
        label={
          <span>
            <TraceableCommentContainer
              qaSpecName={getFormattedPath(
                TOOlTIP_FIELDS.supplementFdaLabelFootNote.fieldPath
              )}
              qaSpecFieldName='Footer Note'
            >
              <QaSpecHelpContentLabel
                fieldPath={TOOlTIP_FIELDS.supplementFdaLabelFootNote.fieldPath}
                isEnabled={isQaSpecType}
              >
                Footer Note
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
            <Tooltip title={intl.formatMessage(Messages.supplementFooterNote)}>
              <FileTextOutlined
                onClick={handleFooterNote}
                style={{ marginLeft: '5px' }}
              />
            </Tooltip>
          </span>
        }
        rules={[
          getRequiredRule({
            fieldName: ['fdaLabelFootNote'],
            displayName: 'Footer Note',
          }),
        ]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>
      <Form.Item
        name='otherIngredients'
        label={
          <TraceableCommentContainer
            qaSpecName={getFormattedPath(
              TOOlTIP_FIELDS.supplementOtherIngredients.fieldPath
            )}
            qaSpecFieldName={intl.formatMessage(
              Messages.supplementOtherIngredients
            )}
          >
            <QaSpecHelpContentLabel
              fieldPath={TOOlTIP_FIELDS.supplementOtherIngredients.fieldPath}
              isEnabled={isQaSpecType}
            >
              {intl.formatMessage(Messages.supplementOtherIngredients)}
            </QaSpecHelpContentLabel>
          </TraceableCommentContainer>
        }
        rules={[
          getRequiredRule({
            fieldName: ['otherIngredients'],
            displayName: intl.formatMessage(
              Messages.supplementOtherIngredients
            ),
          }),
        ]}
      >
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item
        name='languageCode'
        label={
          <TraceableCommentContainer
            qaSpecName={getFormattedPath(
              TOOlTIP_FIELDS.supplementLanguageCode.fieldPath
            )}
            qaSpecFieldName='Language Code'
          >
            <QaSpecHelpContentLabel
              fieldPath={TOOlTIP_FIELDS.supplementLanguageCode.fieldPath}
              isEnabled={isQaSpecType}
            >
              Language Code
            </QaSpecHelpContentLabel>
          </TraceableCommentContainer>
        }
        rules={[
          getRequiredRule({
            fieldName: ['languageCode'],
            displayName: 'Language Code',
          }),
        ]}
      >
        <WrapperSelect
          showSearch
          filterOption={(input, option) =>
            option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
          }
          optionFilterProp='children'
          getPopupContainer={(triggerNode) => {
            return triggerNode;
          }}
        >
          {sortBy(languageCodeEnum?.[0]?.enumProperties, [
            'enumDisplayName',
            'enumGroup',
          ])?.map((item, index) => (
            <Select.Option value={item?.enumCode} key={index}>
              {`${item.enumDescription} - (${item.enumCode.toUpperCase()})`}
            </Select.Option>
          ))}
        </WrapperSelect>
      </Form.Item>
    </div>
  );
};

export default SupplementForm;
